import type React from "react";

import { defaultColorPalettes } from "@framework/utils/colorPalettes";
import { ColorPaletteSelector, ColorPaletteSelectorType } from "@lib/components/ColorPaletteSelector";
import type { ColorPalette } from "@lib/utils/ColorPalette";
import { ColorSet } from "@lib/utils/ColorSet";

import type {
    CustomSettingImplementation,
    OverriddenValueRepresentationArgs,
    SettingComponentProps,
} from "../../interfacesAndTypes/customSettingImplementation";

type ValueType = ColorSet | null;

export class ColorSetSetting implements CustomSettingImplementation<ValueType, ValueType> {
    defaultValue: ValueType = new ColorSet(defaultColorPalettes[0]);

    mapInternalToExternalValue(internalValue: ValueType): ValueType {
        return internalValue;
    }

    getIsStatic(): boolean {
        return true;
    }

    isValueValidStructure(value: unknown): value is ValueType {
        if (value === null) {
            return true;
        }

        // ColorSet is a class instance, check if it has the expected methods
        return typeof value === "object" && value !== null && "getColorPalette" in value;
    }

    isValueValid(): boolean {
        return true;
    }

    serializeValue(value: ValueType): string {
        const serializedValue = value?.serialize();
        return JSON.stringify(serializedValue);
    }

    deserializeValue(serializedValue: string): ValueType {
        const parsedValue = JSON.parse(serializedValue);
        return ColorSet.fromSerialized(parsedValue);
    }

    makeComponent(): (props: SettingComponentProps<ValueType>) => React.ReactNode {
        return function ColorScaleSelectorDialog(props: SettingComponentProps<ValueType>) {
            function handleColorPaletteChange(value: ColorPalette) {
                const newColorSet = new ColorSet(value);
                props.onValueChange(newColorSet);
            }

            return (
                <ColorPaletteSelector
                    selectedColorPaletteId={props.value?.getColorPalette().getId() ?? ""}
                    colorPalettes={defaultColorPalettes}
                    type={ColorPaletteSelectorType.Categorical}
                    onValueChange={handleColorPaletteChange}
                    disabled={props.disabled}
                />
            );
        };
    }

    overriddenValueRepresentation({ value }: OverriddenValueRepresentationArgs<ValueType>): React.ReactNode {
        if (value === null) {
            return "-";
        }
        return (
            <ColorPaletteSelector
                selectedColorPaletteId={value.getColorPalette().getId()}
                colorPalettes={defaultColorPalettes}
                type={ColorPaletteSelectorType.Categorical}
            />
        );
    }
}
